/**
 * Close a modal and wait until it has actually gone.
 *
 * Bootstrap will not open one modal while another is still hiding: the
 * second `show` is swallowed and the reader is left looking at the page with
 * no dialog at all. Handing over from one transaction form to the next hits
 * this every time, and in two ways — the short form and the full editor are
 * different dialogs, and re-opening the full editor on top of itself stacks
 * a second set of handlers on the same static elements, so the next click
 * fires twice against two different transactions.
 *
 * `hidden.bs.modal` is the event that says the transition is over. The
 * timeout is a floor rather than a guess: a dialog that was never shown
 * fires nothing at all, and waiting for ever on it would be worse than
 * opening a moment early.
 */
export function closeModalAndWait(
  dialog: HTMLElement | null,
  hide: (el: HTMLElement) => void,
  timeoutMs = 600,
): Promise<void> {
  if (!dialog) return Promise.resolve();
  if (!stillOnScreen(dialog)) { hide(dialog); return Promise.resolve(); }
  return new Promise<void>((resolve) => {
    let settled = false;
    const done = (): void => {
      if (settled) return;
      settled = true;
      dialog.removeEventListener('hidden.bs.modal', done);
      resolve();
    };
    dialog.addEventListener('hidden.bs.modal', done);
    hide(dialog);
    setTimeout(done, timeoutMs);
  });
}

/**
 * Is this dialog still occupying the screen — shown, or part-way through
 * going?
 *
 * The `show` class alone is not that question, and reading it as if it were
 * is what broke stepping to the next transaction after a save. Bootstrap
 * takes `show` off at the START of hiding, to run the fade out; it stays
 * mid-transition, and refuses a `show()` for the whole of it. So a caller
 * that had already asked the form to close — which is exactly what saving
 * does — saw no `show` class, concluded there was nothing to wait for, and
 * opened the next one into a dialog that was still busy closing. Bootstrap
 * swallowed it, and the reader was left on the page with no form at all:
 * the change saved, and nothing stepped to.
 *
 * The inline `display` is what distinguishes the two. Bootstrap sets it to
 * `block` when it shows a dialog and back to `none` only once the hide has
 * finished, so `block` without `show` is precisely "still going".
 */
function stillOnScreen(dialog: HTMLElement): boolean {
  return dialog.classList.contains('show') || dialog.style.display === 'block';
}
